import { Admin, Resource, CustomRoutes, type CoreLayoutProps } from 'react-admin';
import { Route } from 'react-router-dom';
import { authFeathersProvider, dataFeathersProvider, resources } from '@repo/c360-ra-components';
import { QueryClient } from '@tanstack/react-query';
import { Layout } from './Layout';
import { AccessDenied, LoginPage } from './components';
import { Profile } from './pages/Profile';
import { Dashboard } from './pages/Dashboard';
import {
  AdminUsers,
  ClientUsers,
  GuardianCustomers,
  Roles,
  RecurringPayments,
  Policies,
  RecurringPaymentsExecutionLogs,
  TroubleshootingGuidelines,
  PaymentCards,
  CustomerApplications,
  ApplicantProfiles,
  RiskProfiles,
  BlacklistRecords,
  SignupAttemptHistoryLogs,
  MaxSignupRecords,
  AuditLogs,
  SigninAttemptHistoryLogs,
  Documents,
  TaReport,
  Docusign,
  OrderRecords,
  NonOnlineRenewals,
  SalespalApplications,
  ManualMergeRecords,
  OnfidoVerifications,
  ManualPaymentExecutionLogs,
  BankCards,
  FacRecords,
} from './resources';
import { type FunctionComponent } from 'react';
import { DefaultTheme } from './themes';
import { feathersClient } from './client';

const authClientConfig = {
  storageKey: 'feathers-jwt',
  authenticate: { strategy: 'local' },
  idKey: '_id',
  authServiceName: resources.adminUsers,
  logoutOnForbidden: false,
};

// Use HTTP PATCH method instead of PUT to implement UPDATE and _id as the id field
const restClientConfig = { usePatch: true, idKey: '_id' };

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      structuralSharing: false,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retryDelay: 10000,
    },
  },
}) as any;

export const App = () => (
  //admin component for the app
  <Admin
    title="C360 Admin Portal"
    dashboard={Dashboard}
    queryClient={queryClient}
    dataProvider={dataFeathersProvider(feathersClient, restClientConfig)}
    authProvider={authFeathersProvider(feathersClient, authClientConfig)}
    layout={Layout as FunctionComponent<CoreLayoutProps>}
    loginPage={LoginPage}
    accessDenied={AccessDenied}
    theme={DefaultTheme}
    requireAuth //calls the checkAuth
  >
    <Resource {...AdminUsers} name={resources.adminUsers} options={{ label: 'Admin Users' }} />
    <Resource {...ClientUsers} name={resources.clientUsers} options={{ label: 'Client Users' }} />
    <Resource {...GuardianCustomers} name={resources.guardianCustomers} options={{ label: 'Guardian Customers' }} />
    <Resource {...Roles} name={resources.roles} options={{ label: 'Roles and Permissions' }} />
    <Resource {...Policies} name={resources.policies} options={{ label: 'Policies' }} />
    <Resource {...PaymentCards} name={resources.paymentCards} options={{ label: 'Payment Cards' }} />
    <Resource {...RecurringPayments} name={resources.recurringPayments} options={{ label: 'Recurring Payments' }} />;
    <Resource {...RecurringPaymentsExecutionLogs} name={resources.recurringPaymentsExecutionLogs} options={{ label: 'Recurring Payments Execution Logs' }} />;
    <Resource {...TroubleshootingGuidelines} name={resources.troubleshootingGuidelines} options={{ label: 'Troubleshooting Guidelines' }} />;
    <Resource {...CustomerApplications} name={resources.customerApplications} options={{ label: 'Applications' }} />;
    <Resource {...ApplicantProfiles} name={resources.applicants} options={{ label: 'Applicant Profiles' }} />;
    <Resource {...RiskProfiles} name={resources.riskProfiles} options={{ label: 'Risk Profiles' }} />;
    <Resource {...BlacklistRecords} name={resources.blacklistRecords} options={{ label: 'Blacklist Records' }} />;
    <Resource {...SignupAttemptHistoryLogs} name={resources.signupAttemptHistoryLogs} options={{ label: 'Signup Attempt History Logs' }} />;
    <Resource {...Documents} name={resources.documents} options={{ label: 'Documents' }} />;
    <Resource {...MaxSignupRecords} name={resources.maxSignupRecords} options={{ label: 'Max Signup Records' }} />;
    <Resource {...AuditLogs} name={resources.auditLogs} options={{ label: 'Audit Logs' }} />;
    <Resource {...SigninAttemptHistoryLogs} name={resources.signinAttemptHistoryLogs} options={{ label: 'Signin Attempt History Logs' }} />;
    <Resource {...TaReport} name={resources.taReports} options={{ label: 'Transaction Assurance Records' }} />;
    <Resource {...Docusign} name={resources.docusign} options={{ label: 'DocuSign' }} />;
    <Resource {...OrderRecords} name={resources.orderRecords} options={{ label: 'Orders Records' }} />;
    <Resource {...NonOnlineRenewals} name={resources.nonOnlineRenewals} options={{ label: 'Non Online Renewals' }} />;
    <Resource {...SalespalApplications} name={resources.salespalApplications} options={{ label: 'Salespal Applications' }} />;
    <Resource {...ManualMergeRecords} name={resources.manualMerges} options={{ label: 'Manual Merge Records' }} />;
    <Resource {...OnfidoVerifications} name={resources.onfidoVerifications} options={{ label: 'Onfido Verifications' }} />;
    <Resource {...ManualPaymentExecutionLogs} name={resources.manualPaymentExecutionLogs} options={{ label: ' Payment Execution Logs' }} />;
    <Resource {...BankCards} name={resources.bankCards} options={{ label: 'Bank Cards' }} />;
    <Resource {...FacRecords} name={resources.facRecords} options={{ label: 'FAC Records' }} />;
    <CustomRoutes>
      <Route path="/profile" element={<Profile />} />
    </CustomRoutes>
  </Admin>
);
